import { Box, Stack } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { colors } from '../../components';
import CommonTabs from '../../components/tabComponent/commonTabComponent';
import CommonHeader from '../../components/ui-components/CommonHeader';
import { getSupportContractList, getSupportRequestTypes, SupportRequestType } from '../../services/support-service';
import { getProfileDetails, updateUserDetails, uploadProfilePicture } from '../../services/user-service';
import { RootState } from '../../store';
import { hideLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { setLoginUserProfileImageUrl, updateUserProfileDetails } from '../../store/reducers/userSlice';
import {
    trackProfileDetailsSaved,
    trackProfilePictureUpdated,
    trackProfileUpdateFailed,
} from '../../utils/google-analytics';
import { restrictedExtensions } from '../../utils/helper';
import { useTranslation } from '../../utils/translations';

import { ChangePasswordTab } from './ChangePasswordTab';
import { MyProfileTab } from './MyProfileTab';
import { NotificationsTab } from './NotificationsTab';
import { SettingsWrapper } from './SettingStyles';

interface ContractListItem {
    ContractId?: string | number;
    AspireContractId?: string;
}

export const Settings: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const impersonation = useSelector((state: RootState) => state.impersonation);
    // user-details has no IsPrimary; a user is secondary when they have contracts and none is primary
    const isPrimary = useSelector((state: RootState) => {
        const userContracts = state.contracts?.contracts || [];
        return (
            state.user.userDetails?.IsPrimary !== false &&
            (!userContracts.length || userContracts.some(c => c.IsPrimary))
        );
    });
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [mobileNumber, setMobileNumber] = useState('');
    const [isEditMode, setIsEditMode] = useState(
        (location?.state as { enableEditMode?: boolean } | null)?.enableEditMode ?? false,
    );
    const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState(false);
    const [verifiedAlternatePhoneNumber, setVerifiedAlternatePhoneNumber] = useState(false);
    const [alternativeMobileNumber, setAlternativeMobileNumber] = useState('');
    const [originalMobileNumber, setOriginalMobileNumber] = useState('');
    const [originalAlternativeMobileNumber, setOriginalAlternativeMobileNumber] = useState('');
    const [originalFirstName, setOriginalFirstName] = useState('');
    const [originalLastName, setOriginalLastName] = useState('');
    const [firstNameError, setFirstNameError] = useState('');
    const [lastNameError, setLastNameError] = useState('');
    const [alternativePhoneError, setAlternativePhoneError] = useState('');
    const [mobileNumberError, setMobileNumberError] = useState('');
    const [address, setAddress] = useState({
        street: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
    });
    const [requestTypes, setRequestTypes] = useState<SupportRequestType[]>([]);
    const [contracts, setContracts] = useState<Array<{ id: string; name: string }>>([]);

    // Check if phones have changed and need verification
    const isPrimaryPhoneChanged = mobileNumber !== originalMobileNumber;
    const isAlternatePhoneChanged = alternativeMobileNumber !== originalAlternativeMobileNumber;

    // Verification status comes from API (IsVerified) and is updated when user verifies; show Verify button when not verified
    const effectivePrimaryVerified = verifiedPhoneNumber;
    const effectiveAlternateVerified = verifiedAlternatePhoneNumber;

    // Save button should be disabled if:
    // 1. Primary phone was changed and not verified, OR
    // 2. Alternate phone was changed and not verified
    // If phone numbers haven't changed, Save button should be enabled
    const isSaveDisabled =
        (isPrimaryPhoneChanged && !effectivePrimaryVerified && !firstNameError) ||
        (isAlternatePhoneChanged && !effectiveAlternateVerified && !lastNameError);

    const initialUserDetailsFetch = useRef(false);

    const getUserDetails = useCallback(async () => {
        try {
            const response = await getProfileDetails();
            const userDetails = response?.data?.data;
            setFirstName(userDetails?.FirstName);
            setLastName(userDetails?.LastName);
            setOriginalFirstName(userDetails?.FirstName);
            setOriginalLastName(userDetails?.LastName);
            setEmail(userDetails?.Email);

            // Find primary and alternate phone numbers by checking IsPrimary flag
            interface PhoneEntry {
                IsPrimary: boolean;
                PhoneNumber: string;
                IsVerified: boolean;
            }
            const primaryPhoneObj = userDetails?.phoneNumbers?.find((phone: PhoneEntry) => phone?.IsPrimary === true);
            const alternatePhoneObj = userDetails?.phoneNumbers?.find(
                (phone: PhoneEntry) => phone?.IsPrimary === false,
            );
            const primaryPhone = primaryPhoneObj?.PhoneNumber || '';
            const alternatePhone = alternatePhoneObj?.PhoneNumber || '';
            setMobileNumber(primaryPhone);
            setOriginalMobileNumber(primaryPhone);
            setAlternativeMobileNumber(alternatePhone);
            setOriginalAlternativeMobileNumber(alternatePhone);

            // Set verification status from API IsVerified field for each phone number
            setVerifiedPhoneNumber(primaryPhoneObj?.IsVerified === true);
            setVerifiedAlternatePhoneNumber(alternatePhoneObj?.IsVerified === true);

            // Set address
            setAddress({
                street: userDetails?.Street || '',
                city: userDetails?.City || '',
                state: userDetails?.State || '',
                country: userDetails?.Country || '',
                postalCode: userDetails?.PostalCode || '',
            });

            // Set profile image if available
            if (userDetails?.ProfileImage) {
                dispatch(
                    setLoginUserProfileImageUrl(`${process.env.REACT_APP_PROFILE_URL}${userDetails.ProfileImage}`),
                );
            }

            // Update user details in Redux store
            dispatch(updateUserProfileDetails(userDetails));
        } catch (error) {
            console.error('Error getting user details:', error);
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch]);

    const fetchContracts = useCallback(async () => {
        try {
            const response = await getSupportContractList(false);
            if (response.statusCode === 200 && Array.isArray(response.data)) {
                const transformedContracts = (response.data as ContractListItem[]).map(item => ({
                    id: item.ContractId?.toString() || '',
                    name: item.AspireContractId || '',
                }));
                setContracts(transformedContracts);
            }
        } catch (error) {
            console.error('Error fetching contracts:', error);
        }
    }, []);

    const fetchRequestTypes = useCallback(async () => {
        try {
            const response = await getSupportRequestTypes(false);
            if (response.statusCode === 200 && Array.isArray(response.data)) {
                setRequestTypes(response.data);
            }
        } catch (error) {
            console.error('Error fetching request types:', error);
        }
    }, []);

    useEffect(() => {
        if (initialUserDetailsFetch.current) return;
        initialUserDetailsFetch.current = true;

        const fetchAllData = async () => {
            await Promise.all([getUserDetails(), fetchContracts(), fetchRequestTypes()]);
        };

        fetchAllData();
    }, [getUserDetails, fetchContracts, fetchRequestTypes]);

    const handleTabChange = (newIndex: number) => {
        setActiveTabIndex(newIndex);
    };

    const handleProfilePictureUpload = async (file: File) => {
        // Get file extension for additional security check
        const fileName = file.name.toLowerCase();
        const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1);

        // Check if file extension is restricted (additional security layer)
        if (restrictedExtensions.includes(fileExtension)) {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: `Security: File type .${fileExtension} is Not Allowed. Please Upload Only Image Files (JPEG, PNG, WebP).`,
                    severity: 'error',
                }),
            );
            return;
        }

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: 'Unsupported File Format. Please Upload a JPEG, PNG, or WebP Image.',
                    severity: 'error',
                }),
            );
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: 'File Size Exceeds the 5 MB Limit. Please Upload a Smaller File.',
                    severity: 'error',
                }),
            );
            return;
        }

        try {
            dispatch({ type: 'app/showLoader' });
            const response = await uploadProfilePicture(file, true);
            if (response.statusCode === 200) {
                // Track profile update
                trackProfilePictureUpdated();

                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || 'Profile Picture Uploaded Successfully',
                        severity: 'success',
                    }),
                );
                // Update profile image URL if returned
                if (response.data?.profileImageUrl) {
                    dispatch(
                        setLoginUserProfileImageUrl(
                            `${process.env.REACT_APP_PROFILE_URL}${response.data.profileImageUrl}`,
                        ),
                    );
                }
                // Refresh user details to get updated profile image
                await getUserDetails();
            } else {
                trackProfileUpdateFailed('profile_picture', response.message);
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || 'Failed to Upload Profile Picture',
                        severity: 'error',
                    }),
                );
            }
        } catch (error: unknown) {
            const errMessage =
                error instanceof Error ? error.message : 'Error Uploading Profile Picture. Please Try Again.';
            trackProfileUpdateFailed('profile_picture', errMessage);
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: errMessage,
                    severity: 'error',
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleEditDetails = () => {
        setIsEditMode(true);
        // Don't reset verification status - it should be based on whether phone matches original
        // Only reset if phone number has been changed from original
        if (mobileNumber !== originalMobileNumber) {
            setVerifiedPhoneNumber(false);
        }
        if (alternativeMobileNumber !== originalAlternativeMobileNumber) {
            setVerifiedAlternatePhoneNumber(false);
        }
    };

    const handleSaveDetails = async () => {
        // Clear previous errors
        setFirstNameError('');
        setLastNameError('');
        setAlternativePhoneError('');
        setMobileNumberError('');

        // Validate first name
        if (!firstName || firstName.trim() === '') {
            setFirstNameError('First Name Is Required');
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: 'First Name Is Required',
                    severity: 'error',
                }),
            );
            return;
        }

        // Validate last name
        if (!lastName || lastName.trim() === '') {
            setLastNameError('Last Name Is Required');
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: 'Last Name Is Required',
                    severity: 'error',
                }),
            );
            return;
        }

        // Validate phone numbers are not the same
        if (
            alternativeMobileNumber &&
            alternativeMobileNumber.trim() !== '' &&
            mobileNumber === alternativeMobileNumber
        ) {
            setAlternativePhoneError('Alternate Phone Number Cannot Be The Same As Primary Phone Number');
            setMobileNumberError('Phone Number Cannot Be The Same As Alternate Phone Number');
            return;
        }

        try {
            // Check if firstName or lastName changed
            const hasNameChanged = firstName !== originalFirstName || lastName !== originalLastName;

            if (hasNameChanged) {
                dispatch({ type: 'app/showLoader' });
                const response = await updateUserDetails({
                    firstName,
                    lastName,
                });

                if (response.statusCode === 200) {
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: response.message || 'Your Details Have Been Updated Successfully.',
                            severity: 'success',
                        }),
                    );
                    // Update original values after successful save
                    setOriginalFirstName(firstName);
                    setOriginalLastName(lastName);

                    // Refresh user details to get latest data
                    await getUserDetails();
                } else {
                    trackProfileUpdateFailed('details', response.message);
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: response.message || 'Failed To Update User Details',
                            severity: 'error',
                        }),
                    );
                    return; // Don't exit edit mode if update failed
                }
            }

            trackProfileDetailsSaved();
            setIsEditMode(false);
            // Refresh user details so verification state (IsVerified) comes from API
            await getUserDetails();
        } catch (error: unknown) {
            const errMessage =
                error instanceof Error ? error.message : 'Error Updating User Details. Please Try Again.';
            trackProfileUpdateFailed('details', errMessage);
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: errMessage,
                    severity: 'error',
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
    };

    return (
        <SettingsWrapper height={{ xs: 'auto', xl: '100%' }} p={3}>
            <Stack gap={3} height={{ xs: 'auto', xl: '100%' }} sx={{ flex: 1 }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                    <CommonHeader
                        mainTitle={t('settings.header.mainTitle')}
                        subTitle={t('settings.header.subTitle')}
                        icon="icon-settings"
                    />
                </Stack>

                {/* Tabs Navigation and Content */}
                <CommonTabs
                    tabs={[
                        {
                            label: t('settings.navigation.myProfile') || 'My Profile',
                            content: (
                                <Box
                                    sx={{
                                        borderRadius: 4,
                                        p: 3,
                                        bgcolor: colors.fullWhite,
                                        border: `1px solid${colors.greyPreset[20]}`,
                                    }}
                                >
                                    <MyProfileTab
                                        firstName={firstName}
                                        lastName={lastName}
                                        email={email}
                                        mobileNumber={mobileNumber}
                                        onFirstNameChange={(value: string) => {
                                            setFirstName(value);
                                            if (firstNameError) setFirstNameError('');
                                        }}
                                        onLastNameChange={(value: string) => {
                                            setLastName(value);
                                            if (lastNameError) setLastNameError('');
                                        }}
                                        firstNameError={firstNameError}
                                        lastNameError={lastNameError}
                                        alternativePhoneError={alternativePhoneError}
                                        mobileNumberError={mobileNumberError}
                                        onSetMobileNumberError={setMobileNumberError}
                                        onMobileNumberChange={(value: string) => {
                                            setMobileNumber(value);
                                            if (mobileNumberError) setMobileNumberError('');
                                            // Reset verification status if phone number changes from original
                                            if (value !== originalMobileNumber) {
                                                setVerifiedPhoneNumber(false);
                                            }
                                        }}
                                        onProfilePictureUpload={handleProfilePictureUpload}
                                        isEditMode={isEditMode}
                                        alternativeMobileNumber={alternativeMobileNumber}
                                        onAlternativeMobileNumberChange={(value: string) => {
                                            setAlternativeMobileNumber(value);
                                            if (alternativePhoneError) setAlternativePhoneError('');
                                            // Reset verification status if phone number changes from original
                                            if (value !== originalAlternativeMobileNumber) {
                                                setVerifiedAlternatePhoneNumber(false);
                                            }
                                        }}
                                        verifiedPhoneNumber={effectivePrimaryVerified}
                                        onVerifyPhoneNumber={() => {
                                            setVerifiedPhoneNumber(true);
                                        }}
                                        verifiedAlternatePhoneNumber={effectiveAlternateVerified}
                                        onVerifyAlternatePhoneNumber={() => {
                                            setVerifiedAlternatePhoneNumber(true);
                                        }}
                                        onRefreshUserDetails={getUserDetails}
                                        onEditDetails={handleEditDetails}
                                        onSaveDetails={handleSaveDetails}
                                        isSaveDisabled={isSaveDisabled}
                                        onSetAlternativePhoneError={setAlternativePhoneError}
                                        address={address}
                                        requestTypes={requestTypes}
                                        contracts={contracts}
                                        isPrimary={isPrimary}
                                    />
                                </Box>
                            ),
                        },
                        {
                            label: t('settings.navigation.notifications') || 'Notifications',
                            content: (
                                <Box
                                    sx={{
                                        borderRadius: 4,
                                        p: 3,
                                        bgcolor: colors.fullWhite,
                                        border: `1px solid${colors.greyPreset[20]}`,
                                    }}
                                >
                                    <NotificationsTab />
                                </Box>
                            ),
                        },
                        {
                            label: t('settings.navigation.changePassword') || 'Change Password',
                            content: (
                                <Box
                                    sx={{
                                        borderRadius: 4,
                                        p: 3,
                                        bgcolor: colors.fullWhite,
                                        border: `1px solid${colors.greyPreset[20]}`,
                                    }}
                                >
                                    <ChangePasswordTab />
                                </Box>
                            ),
                            sx: {
                                pointerEvents: impersonation?.isImpersonating ? 'none' : 'auto',
                                cursor: impersonation?.isImpersonating ? 'not-allowed' : 'pointer',
                            },
                        },
                    ]}
                    defaultActiveIndex={activeTabIndex}
                    onTabChange={handleTabChange}
                />
            </Stack>
        </SettingsWrapper>
    );
};
